import {
  CarManager,
  ConstructionManager,
  EconomyManager,
  EventBus,
  FacilityManager,
  FoodSourceManager,
  FreightManager,
  HouseManager,
  LabourManager,
  MercyManager,
  FiscalManager,
  WelfareManager,
  ParkingtonSurfaceAntController,
  PheromoneGrid,
  PowerManager,
  TerrainGrid,
  WaterManager,
} from '@antification/core';
import { Portal } from '@antification/protocol';
import { Camera } from './rendering/Camera';

/**
 * Available tools in the Parkington layer.
 *
 * The last three are §10's mercy builds, and they sit in this list rather than in a
 * panel of their own on purpose. `IMPLEMENTATION-GOVERNORS.md` §6 asks for them
 * *"priced and unremarked in the ordinary build toolbar"* — the same row of buttons
 * as the road brush, the same one-line description, no heading that says these ones
 * are different. A mayor who wants to know what a covenant is worth has to work it
 * out; nothing in this interface will tell them, because nothing in the simulation
 * knows.
 */
export enum ParkingtonTool {
  SELECT = 'select',
  PRECISION = 'precision',  // 1x1 brush for precise cuts
  HIGHWAY = 'highway',      // 3x3 brush for road building
  COVENANT = 'covenant',    // 1x1: deed a cell wild, permanently
  HEDGEROW = 'hedgerow',    // 1x1: plant a bush that comes in later
  UNDERCROSS = 'undercross',// 1x1: a way under a paved cell
  INTERCHANGE = 'interchange', // 2 cells: where national freight enters the map
  DEMOLISH = 'demolish',    // Future: remove structures
  ZONE = 'zone',            // Future: designate zones
}

/**
 * The tools that act on a cell rather than select one.
 *
 * Seven of them now, and all seven fire on the click. Five also fire once per
 * `mousemove` while the button is held (`DRAG_TOOLS`), which is what makes the
 * price of a mercy build a thing a mayor can spend by accident, and there is no
 * confirmation anywhere: §12's Parcel Manifest — *Law 7 enforced at the moment of
 * choice* — is Phase 4's and does not exist. See ADR-020's open questions.
 */
export const BRUSH_TOOLS: readonly ParkingtonTool[] = Object.freeze([
  ParkingtonTool.PRECISION,
  ParkingtonTool.HIGHWAY,
  ParkingtonTool.COVENANT,
  ParkingtonTool.HEDGEROW,
  ParkingtonTool.UNDERCROSS,
  ParkingtonTool.INTERCHANGE,
]);

/**
 * The tools that keep painting while the button is down.
 *
 * Every brush tool except the interchange. A covenant dragged across a meadow is
 * a meadow deeded cell by cell, which is a sentence somebody could mean; an
 * interchange dragged along a road is a row of $600 structures two cells apart,
 * which is not. A structure is placed, not stroked — and the distinction costs one
 * predicate rather than a modal.
 */
export const DRAG_TOOLS: readonly ParkingtonTool[] = Object.freeze(
  BRUSH_TOOLS.filter((tool) => tool !== ParkingtonTool.INTERCHANGE)
);

/** Whether this tool acts on the cell under the cursor. */
export function isBrushTool(tool: ParkingtonTool): boolean {
  return BRUSH_TOOLS.includes(tool);
}

/** Whether this tool keeps acting while the button is held down. */
export function isDragTool(tool: ParkingtonTool): boolean {
  return DRAG_TOOLS.includes(tool);
}

/**
 * Tool definition with display info.
 */
export interface ToolDefinition {
  id: ParkingtonTool;
  name: string;
  icon: string;
  description: string;
  available: boolean;
  shortcut: string;
}

/**
 * Selected entity info for the detail pane.
 */
export interface SelectedEntity {
  type: 'car' | 'building' | 'portal';
  id: number;
}

/**
 * Hovered entity for catch indicator.
 */
export interface HoveredEntity {
  type: 'car' | 'building' | 'portal';
  id: number;
}

/**
 * Colony colors for portal rendering.
 */
export const COLONY_PORTAL_COLORS = [
  { r: 139, g: 69, b: 19 },   // Brown (Saddle Brown)
  { r: 47, g: 79, b: 79 },    // Dark (Dark Slate Gray)
  { r: 139, g: 0, b: 0 },     // Red (Dark Red)
] as const;

/**
 * Readonly view of ParkingtonLayer state for use by extracted modules.
 * Modules receive this instead of a direct reference to the orchestrator.
 */
export interface ParkingtonState {
  readonly width: number;
  readonly height: number;
  readonly mouseX: number;
  readonly mouseY: number;
  readonly hoveredCell: { x: number; y: number } | null;
  readonly isMouseDown: boolean;
  readonly inputBlocked: boolean;
  readonly currentTool: ParkingtonTool;
  readonly toolDefinitions: readonly ToolDefinition[];
  readonly selectedEntity: SelectedEntity | null;
  readonly hoveredEntity: HoveredEntity | null;
  readonly trackedCarId: number | null;
  readonly cellsChanged: number;
  readonly precisionUnlocked: boolean;
  readonly currentBrushSize: number;
  readonly showPheromones: boolean;
  readonly showSaveSlots: boolean;
  readonly portalPulse: number;
  readonly portalCells: ReadonlyArray<{ x: number; y: number }>;
  /**
   * Which of those entrances are under a federal slab, by colony index.
   *
   * Parallel to `portalCells` rather than folded into it, because a sealed
   * entrance is still *where the entrance was* — the ants below still walk to it,
   * and the cell is still the one a returning forager is trying to reach. What the
   * flag turns off is the drawing and the click: there is no hole to glow and
   * nothing to look down (ADR-028).
   */
  readonly sealedPortals: ReadonlyArray<boolean>;
  readonly selectedColonyIndex: number;
  readonly isPanMode: boolean;
  readonly panTouchStart: { x: number; y: number } | null;
  readonly autosaveTimer: number;
  readonly selectionPaneCloseArea: { x: number; y: number; w: number; h: number } | null;

  // Performance debug flags
  readonly skipVegetation: boolean;
  readonly skipHouses: boolean;
  readonly skipCars: boolean;
  readonly skipAnts: boolean;
  readonly skipTerrain: boolean;
  readonly skipFoodSources: boolean;
  readonly isActivelyTransforming: boolean;

  /**
   * Where this client is looking.
   *
   * Not a manager and not readonly in the way the rest of this interface is:
   * input handlers pan and zoom it directly. That is deliberate. Everything
   * else here is the world, which a handler may only ask the mayor to change;
   * the camera is the client's own, and no other client — or headless
   * settlement worker — has one (ADR-009).
   */
  readonly camera: Camera;

  // Manager accessors
  readonly grid: TerrainGrid;
  readonly construction: ConstructionManager;
  readonly cars: CarManager;
  readonly houses: HouseManager;
  readonly foodSources: FoodSourceManager;
  readonly pheromones: PheromoneGrid;
  readonly surfaceAntController: ParkingtonSurfaceAntController;
  readonly economy: EconomyManager;
  /**
   * The district's grid connection.
   *
   * Here rather than as a derived flag because two renderers want different
   * things from it: the buildings want to know which of them is dark, and the
   * status panel wants the demand against the supply. Law 2 applied to a flow —
   * *power shortage is houses going dark on someone's screen* — needs the first
   * of those on the map rather than in a number somewhere.
   */
  readonly power: PowerManager;
  /**
   * The district's mains.
   *
   * Here for the same two reasons as the grid: the buildings want to know whose
   * garden went unwatered, and the status panel wants the demand against the
   * supply. A brown lawn is the only thing a water shortage does, so if it were
   * not reachable from a renderer the flow would not exist at all (Law 2).
   */
  readonly water: WaterManager;
  /**
   * The federal estate on this map.
   *
   * The manager rather than a readout, for the same reason as the two flows: the
   * depth-sorted pass needs the footprints to draw the buildings, and the status
   * panel needs the land and the supplied fraction. A facility is the largest
   * object a district can acquire and it is the one thing on the map the mayor
   * cannot touch, so Law 2 wants it visible above all.
   */
  readonly facilities: FacilityManager;
  /**
   * Who works for the polity, and whether they are going in.
   *
   * The manager, on the same terms as the estate beside it: the panel wants a
   * head count and a strike flag, and the keyboard handler needs to know whether
   * the facility under the cursor is already out before it issues a verb. Law 2
   * wants labour visible for a blunter reason than the flows — a family driving
   * to a data centre looks exactly like a family driving to the shops, and the
   * only place the difference can be seen is a panel and a tooltip.
   */
  readonly labour: LabourManager;
  /**
   * How the households are doing, and who is leaving over it.
   *
   * The manager, like the three above it, and for the reason that makes Law 2
   * sharpest here: a district's quality of life is invisible by construction —
   * nothing about a dark house *looks* like a family deciding to go — so the
   * only place the measure exists for a player is a panel line and a name in the
   * ticker. §12's District Vitals is the screen this eventually belongs on.
   */
  readonly welfare: WelfareManager;
  /**
   * What the mayor has bought that pays nothing.
   *
   * The manager, like the four above it, and for a reason none of them has: this is
   * the only one whose contents cannot be inferred from anything else on screen. A
   * dark house looks dark and a slab looks like a slab, but a covenanted meadow looks
   * exactly like a meadow and an undercrossing looks exactly like a road. Law 2 says
   * a consequence has to be visible somewhere, and for these three the *only*
   * somewhere is a mark this renderer draws because it asked this object.
   */
  readonly mercy: MercyManager;
  /**
   * What the district owes the world outside it.
   *
   * The manager, for the same reason as the five above it and with one addition: this
   * is the only object on the list whose state was set by somebody who is not the
   * player. A tithe rate is legible nowhere else — not on the map, not in a tool, not
   * in the budget line, which shows a pool and not why it moved.
   */
  readonly fiscal: FiscalManager;
  /**
   * The district's corridor: its interchanges, and the freight over them.
   *
   * The manager, like the six above it. Two renderers want different things from
   * it — the map wants to draw where the ramps are, and the panel wants the legs
   * and the toll — and there is a third reason none of the others has: a truck
   * looks like a vehicle and an interchange looks like road, so without this object
   * a corridor would be a district that had simply become busier for no visible
   * reason. Law 2 asks for the cause to be somewhere, and this is the only
   * somewhere there is.
   */
  readonly freight: FreightManager;
  /**
   * The season the district is living through. 1 for its first.
   *
   * A number rather than the `SeasonManager` itself, because the panel wants a
   * readout and not a manager. The Chronicle behind it gets a screen of its own
   * when City Hall arrives (Phase 4, `DESIGN-GOVERNORS.md` §12).
   */
  readonly season: number;
  /**
   * Whether a Season Report is on screen right now.
   *
   * View state, and the only thing in this interface that is. The keyboard needs
   * it so that Escape can dismiss the outermost thing first — a card, then a
   * selection — and nothing else reads it.
   */
  readonly seasonReportShown: boolean;
  readonly portals: Portal[];
  readonly events: EventBus;

  // Constants
  readonly PRECISION_UNLOCK: number;
  readonly AUTOSAVE_INTERVAL: number;
  readonly COLONY_COUNT: number;
}
